<template>
    <div class="registration-fields-table">
        <div class="fields-table-container">
        <table class="fields-table">
            <thead>
                <tr>
                    <th class="field-col">Field</th>
                    <th class="control-col">Visibility</th>
                    <th class="control-col">Required</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="field in fieldDefinitions" :key="field.key">
                    <td class="field-col">{{ field.label }}</td>
                    <td class="control-col">
                        <span v-if="!field.configurable" class="fixed-text">Show</span>
                        <SegmentedButtonPair
                            v-else
                            :value="getVisible(field.key)"
                            :options="visibilityOptions"
                            :disabled="disabled"
                            @input="handleVisibilityChange(field.key, $event)"
                        />
                    </td>
                    <td class="control-col">
                        <span v-if="!field.configurable" class="fixed-text">Mandatory</span>
                        <SegmentedButtonPair
                            v-else
                            :value="getRequired(field.key)"
                            :options="requiredOptions"
                            :disabled="disabled || !getVisible(field.key)"
                            @input="handleRequiredChange(field.key, $event)"
                        />
                    </td>
                </tr>
            </tbody>
        </table>

        <!-- Info banner: both Email and Phone optional -->
        <div v-if="showBothOptionalBanner" class="info-banner">
            If phone and email are both set to optional, at least one must be entered during registration.
        </div>
        </div>

        <!-- Best practice note -->
        <div class="note-banner">
            <strong>Note:</strong> It's best practice to include at least 3 identifiers for a member (ex. Name, Date
            of Birth, Phone | Name, Date of Birth, Medicaid ID).
        </div>
    </div>
</template>

<script>
    import SegmentedButtonPair from '@/components/general/inputs/SegmentedButtonPair.vue';

    export default {
        name: 'RegistrationFieldsTable',
        components: { SegmentedButtonPair },
        props: {
            registrationFields: {
                type: Object,
                required: true,
            },
            fieldDefinitions: {
                type: Array,
                required: true,
            },
            disabled: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                visibilityOptions: [
                    { label: 'Show', value: true },
                    { label: 'Hide', value: false },
                ],
                requiredOptions: [
                    { label: 'Mandatory', value: true },
                    { label: 'Optional', value: false },
                ],
            };
        },
        computed: {
            showBothOptionalBanner() {
                const emailField = this.registrationFields.email;
                const phoneField = this.registrationFields.phone;
                return (
                    emailField &&
                    phoneField &&
                    emailField.visible &&
                    phoneField.visible &&
                    !emailField.required &&
                    !phoneField.required
                );
            },
        },
        methods: {
            getVisible(key) {
                return this.registrationFields[key] ? this.registrationFields[key].visible : true;
            },
            getRequired(key) {
                return this.registrationFields[key] ? this.registrationFields[key].required : false;
            },
            handleVisibilityChange(key, value) {
                // Prevent hiding both Email and Phone
                if (!value) {
                    if (key === 'email' && !this.getVisible('phone')) {
                        this.$toasted.error('At least one of Email or Phone must be visible.');
                        return;
                    }
                    if (key === 'phone' && !this.getVisible('email')) {
                        this.$toasted.error('At least one of Email or Phone must be visible.');
                        return;
                    }
                }

                this.$emit('update', key, 'visible', value);

                // When hiding a field, auto-reset required to false
                if (!value) {
                    this.$emit('update', key, 'required', false);
                }
            },
            handleRequiredChange(key, value) {
                this.$emit('update', key, 'required', value);
            },
        },
    };
</script>

<style scoped>
    .registration-fields-table {
        width: 100%;
    }

    .fields-table-container {
        border: 1px solid #e0e0e0;
        border-radius: 12px;
        overflow: hidden;
        padding: 16px;
    }

    .fields-table {
        width: 100%;
        border-collapse: collapse;
    }

    .fields-table th {
        text-align: left;
        padding: 12px 20px;
        font-size: 14px;
        font-weight: 700;
        color: #333;
        border-bottom: 2px solid #5155c3;
    }

    .fields-table td {
        padding: 14px 20px;
        font-size: 14px;
        border-bottom: 1px solid #f0f0f0;
    }

    .fields-table tr:last-child td {
        border-bottom: none;
    }

    .field-col {
        width: 40%;
    }

    .control-col {
        width: 30%;
    }

    .fixed-text {
        font-size: 13px;
        font-weight: 500;
        color: #666;
    }

    .info-banner {
        background: #e8f4fd;
        border-left: 4px solid #5155c3;
        border-radius: 0 8px 8px 0;
        padding: 12px 16px;
        margin-top: 16px;
        font-size: 13px;
        font-style: italic;
        color: #333;
    }

    .note-banner {
        background: #fff8e1;
        border-left: 4px solid #f0a500;
        border-radius: 0 8px 8px 0;
        padding: 12px 16px;
        margin-top: 16px;
        font-size: 13px;
        color: #856404;
    }
</style>
